import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import ImageUploadField from "@/components/ImageUploadField";
import GalleryField from "@/components/GalleryField";
import ContentEditor from "@/pages/admin/ContentEditor";
import TestNotifications from "@/pages/admin/TestNotifications";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutDashboard, Store, Shirt, Package, Settings as SettingsIcon, Plus, Trash2, Check, Pause, Play, Users, Truck, FileText, CreditCard, MessageSquare, ClipboardList, Palette, UserCog, Mail, Pencil, QrCode, Handshake, Coins, XCircle, RotateCcw } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { QRCodeCanvas } from "qrcode.react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const ALL_NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, staff: true },
  { key: "laundries", label: "Laundries", icon: Store, staff: true },
  { key: "prospects", label: "Prospects (CRM)", icon: Users, staff: true },
  { key: "orders", label: "All orders", icon: ClipboardList, staff: true },
  { key: "drivers", label: "Drivers", icon: Truck, staff: true },
  { key: "complaints", label: "Complaints", icon: MessageSquare, staff: true },
  { key: "payouts", label: "Driver payouts", icon: Coins, staff: false },
  { key: "coupons", label: "Coupons & claims", icon: CreditCard, staff: false },
  { key: "bulkemail", label: "Bulk email", icon: Mail, staff: false },
  { key: "analytics", label: "Sales dashboard", icon: LayoutDashboard, staff: false },
  { key: "content", label: "Frontend content", icon: FileText, staff: false },
  { key: "branding", label: "Branding", icon: Palette, staff: false },
  { key: "clothing", label: "Default garments", icon: Shirt, staff: false },
  { key: "packages", label: "Packages", icon: Package, staff: false },
  { key: "emails", label: "Email templates", icon: Mail, staff: false },
  { key: "terms", label: "Terms & Conditions", icon: FileText, staff: false },
  { key: "staff", label: "Team", icon: UserCog, staff: false },
  { key: "settings", label: "Settings & keys", icon: SettingsIcon, staff: false },
  { key: "account", label: "My account", icon: UserCog, staff: true },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const NAV = ALL_NAV.filter((n) => !isStaff || n.staff);
  const [active, setActive] = useState("overview");
  const [stats, setStats] = useState(null);
  const [laundries, setLaundries] = useState([]);
  const [clothing, setClothing] = useState([]);
  const [packages, setPackages] = useState([]);

  const loadLaundries = () => api.get("/admin/laundries").then((r) => setLaundries(r.data));
  const loadClothing = () => api.get("/clothing-types").then((r) => setClothing(r.data));
  const loadPackages = () => api.get("/packages").then((r) => setPackages(r.data));

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    loadLaundries();
    if (!isStaff) { loadClothing(); loadPackages(); }
  }, [isStaff]);

  return (
    <DashboardShell title={isStaff ? "Support staff" : "Site Admin"} nav={NAV} active={active} onSelect={setActive}>
      {active === "overview" && <Overview stats={stats} />}
      {active === "laundries" && <Laundries laundries={laundries} reload={loadLaundries} packages={packages} isStaff={isStaff} />}
      {active === "prospects" && <Prospects reloadLaundries={loadLaundries} />}
      {active === "orders" && <AllOrders />}
      {active === "drivers" && <Drivers />}
      {active === "complaints" && <Complaints />}
      {active === "payouts" && !isStaff && <Payouts />}
      {active === "coupons" && !isStaff && <AdminCoupons />}
      {active === "bulkemail" && !isStaff && <BulkEmail />}
      {active === "analytics" && !isStaff && <Analytics />}
      {active === "content" && !isStaff && <ContentEditor />}
      {active === "branding" && !isStaff && <Branding />}
      {active === "clothing" && !isStaff && <Clothing clothing={clothing} reload={loadClothing} />}
      {active === "packages" && !isStaff && <Packages packages={packages} reload={loadPackages} />}
      {active === "emails" && !isStaff && <EmailTemplates />}
      {active === "terms" && !isStaff && <TermsEditor />}
      {active === "staff" && !isStaff && <Staff />}
      {active === "settings" && !isStaff && <SettingsTab />}
      {active === "account" && <Account />}
    </DashboardShell>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{label}</p>
        <Icon className={`h-5 w-5 ${accent || "text-brand"}`} strokeWidth={1.5} />
      </div>
      <p className="mt-3 font-heading text-3xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function Overview({ stats }) {
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Platform overview</h1>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard icon={Store} label="Laundries" value={stats?.laundries ?? 0} />
        <StatCard icon={Pause} label="Pending approval" value={stats?.pending_laundries ?? 0} accent="text-amber-500" />
        <StatCard icon={Users} label="Customers" value={stats?.customers ?? 0} />
        <StatCard icon={Truck} label="Drivers" value={stats?.drivers ?? 0} />
        <StatCard icon={Package} label="Total orders" value={stats?.orders ?? 0} />
        <StatCard icon={Check} label="Paid orders" value={stats?.paid_orders ?? 0} accent="text-green-600" />
      </div>
    </div>
  );
}

function Laundries({ laundries, reload, packages, isStaff }) {
  const { impersonate } = useAuth();
  const setStatus = async (id, status) => { await api.put(`/admin/laundries/${id}/status`, { status }); await reload(); toast.success(`Marked ${status}`); };
  const del = async (id) => { await api.delete(`/admin/laundries/${id}`); await reload(); toast.success("Laundry removed"); };
  const setPackage = async (id, package_id) => { await api.put(`/admin/laundries/${id}/package`, { package_id }); await reload(); toast.success("Plan updated"); };
  const signInAs = async (id) => {
    try { const { data } = await api.post(`/admin/laundries/${id}/impersonate`); impersonate(data); window.location.href = "/manager"; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const colors = { approved: "border-green-300 text-green-700", pending: "border-amber-300 text-amber-700", suspended: "border-red-300 text-red-700" };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Laundries</h1>
        {!isStaff && <AddLaundryDialog reload={reload} />}
      </div>
      <p className="mt-1 text-slate-500">Approve new shops, suspend or remove them{!isStaff && ", manage their plans and sign in on their behalf"}.</p>
      <div className="mt-6 space-y-4">
        {laundries.map((l) => (
          <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`admin-laundry-${l.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-heading text-lg font-medium text-slate-900">{l.name}</p>
                <p className="mt-1 text-sm text-slate-500">{l.owner?.name} · {l.owner?.email}</p>
                <p className="mt-1 text-sm text-slate-400">{l.city || "No location set"} · {l.item_count} items · {l.used_this_month} orders this month</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge variant="outline" className={`${colors[l.status]} capitalize`} data-testid={`laundry-status-${l.id}`}>{l.status}</Badge>
                <Badge variant="secondary" data-testid={`laundry-plan-${l.id}`}>{l.package_name} plan</Badge>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              {l.status !== "approved" && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setStatus(l.id, "approved")} data-testid={`approve-${l.id}`}><Check className="mr-1.5 h-4 w-4" /> Approve</Button>}
              {l.status === "approved" && <Button size="sm" variant="secondary" onClick={() => setStatus(l.id, "suspended")} data-testid={`suspend-${l.id}`}><Pause className="mr-1.5 h-4 w-4" /> Suspend</Button>}
              {l.status === "suspended" && <Button size="sm" variant="secondary" onClick={() => setStatus(l.id, "approved")}><Play className="mr-1.5 h-4 w-4" /> Reactivate</Button>}
              {!isStaff && (
                <>
                  <Button size="sm" variant="outline" onClick={() => signInAs(l.id)} data-testid={`impersonate-${l.id}`}><UserCog className="mr-1.5 h-4 w-4" /> Sign in as</Button>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Plan:</span>
                    <Select value={l.package_id || ""} onValueChange={(v) => setPackage(l.id, v)}>
                      <SelectTrigger className="h-8 w-36" data-testid={`laundry-package-select-${l.id}`}><SelectValue placeholder="Assign plan" /></SelectTrigger>
                      <SelectContent>{(packages || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(l.id)} data-testid={`delete-laundry-${l.id}`}><Trash2 className="mr-1.5 h-4 w-4" /> Remove</Button>
            </div>
            {!isStaff && <PartnerControl laundry={l} reload={reload} />}
            {!isStaff && <CoverControl laundry={l} reload={reload} />}
          </div>
        ))}
        {laundries.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">No laundries registered yet.</div>}
      </div>
    </div>
  );
}

function AddLaundryDialog({ reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ laundry_name: "", city: "", address: "", postcode: "", lat: "", lng: "", manager_name: "", manager_email: "", manager_password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.laundry_name || !f.manager_name || !f.manager_email) return toast.error("Laundry name, manager name and email are required");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/laundries", { ...f, lat: f.lat ? parseFloat(f.lat) : null, lng: f.lng ? parseFloat(f.lng) : null, origin_url: window.location.origin });
      await reload(); toast.success(data.set_password_email_sent ? "Laundry created — set-password email sent to manager" : "Laundry created & approved"); setOpen(false);
      setF({ laundry_name: "", city: "", address: "", postcode: "", lat: "", lng: "", manager_name: "", manager_email: "", manager_password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-hover" data-testid="add-laundry-btn"><Plus className="mr-2 h-4 w-4" /> Add laundry</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add a laundry & manager</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Laundry name</Label><Input value={f.laundry_name} onChange={set("laundry_name")} data-testid="add-laundry-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>City</Label><Input value={f.city} onChange={set("city")} data-testid="add-laundry-city" /></div>
            <div className="space-y-1.5"><Label>Postcode</Label><Input value={f.postcode} onChange={set("postcode")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Address</Label><Input value={f.address} onChange={set("address")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Latitude</Label><Input value={f.lat} onChange={set("lat")} placeholder="51.50" /></div>
            <div className="space-y-1.5"><Label>Longitude</Label><Input value={f.lng} onChange={set("lng")} placeholder="-0.12" /></div>
          </div>
          <div className="border-t border-slate-100 pt-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Manager login</div>
          <div className="space-y-1.5"><Label>Manager name</Label><Input value={f.manager_name} onChange={set("manager_name")} data-testid="add-manager-name" /></div>
          <div className="space-y-1.5"><Label>Manager email</Label><Input type="email" value={f.manager_email} onChange={set("manager_email")} data-testid="add-manager-email" /></div>
          <div className="space-y-1.5"><Label>Temporary password (optional)</Label><Input value={f.manager_password} onChange={set("manager_password")} data-testid="add-manager-password" placeholder="Leave blank to email a set-password link" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover" data-testid="save-laundry-btn">Create laundry</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CoverControl({ laundry, reload }) {
  const save = async (url) => {
    try { await api.put(`/admin/laundries/${laundry.id}/cover`, { cover_image: url }); await reload(); toast.success(url ? "Cover photo updated" : "Cover photo removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update cover photo"); }
  };
  const saveGallery = async (gallery) => {
    try { await api.put(`/admin/laundries/${laundry.id}/gallery`, { gallery }); await reload(); toast.success("Gallery updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update gallery"); }
  };
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`cover-control-${laundry.id}`}>
      <ImageUploadField label="Cover photo" value={laundry.cover_image} onChange={save} hideUrl testid={`cover-${laundry.id}`} uploadedMsg="Uploaded" />
      <GalleryField label="Shop photos" value={laundry.gallery} onChange={saveGallery} testid={`gallery-${laundry.id}`} />
    </div>
  );
}

function PartnerControl({ laundry, reload }) {
  const [on, setOn] = useState(!!laundry.is_partner);
  const [radius, setRadius] = useState(laundry.service_radius_km ?? "");
  const [rate, setRate] = useState(laundry.partner_commission_rate ?? "");
  const [saving, setSaving] = useState(false);

  const save = async (nextOn) => {
    setSaving(true);
    try {
      await api.put(`/admin/laundries/${laundry.id}/partner`, {
        is_partner: nextOn,
        service_radius_km: radius === "" ? null : parseFloat(radius),
        partner_commission_rate: rate === "" ? null : parseFloat(rate),
      });
      await reload();
      toast.success(nextOn ? "Marked as partner laundry" : "Partner status removed");
    } catch { toast.error("Could not update partner status"); }
    finally { setSaving(false); }
  };

  const toggle = () => { const next = !on; setOn(next); save(next); };

  return (
    <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50/50 p-3" data-testid={`partner-control-${laundry.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={toggle} disabled={saving} data-testid={`partner-toggle-${laundry.id}`}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${on ? "bg-amber-600 text-white" : "bg-white text-amber-700 ring-1 ring-amber-300"}`}>
          <Handshake className="h-4 w-4" /> {on ? "Partner laundry" : "Make partner"}
        </button>
        {on && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Service radius (km)</Label>
              <Input className="h-8 w-28" type="number" step="0.5" value={radius} onChange={(e) => setRadius(e.target.value)} placeholder="8" data-testid={`partner-radius-${laundry.id}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Commission (%)</Label>
              <Input className="h-8 w-28" type="number" step="0.5" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="15" data-testid={`partner-rate-${laundry.id}`} />
            </div>
            <Button size="sm" variant="secondary" disabled={saving} onClick={() => save(true)} data-testid={`partner-save-${laundry.id}`}>Save</Button>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-amber-700/80">Partner laundries catch out-of-area orders and are charged the higher partner commission.</p>
    </div>
  );
}

function openInvoice(o) {
  const rows = o.items.map((li) => `<tr><td>${li.qty} × ${li.name}</td><td style="text-align:right">£${(li.qty * li.price).toFixed(2)}</td></tr>`).join("");
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>Invoice ${o.id.slice(0,8)}</title><style>body{font-family:Arial;padding:40px;color:#0f172a}h1{color:#2563EB}table{width:100%;border-collapse:collapse;margin-top:20px}td{padding:8px 0;border-bottom:1px solid #eee}.t{font-weight:700;font-size:18px}</style></head><body><h1>Just Laundry</h1><p>Invoice <b>#${o.id.slice(0,8).toUpperCase()}</b> · ${(o.created_at||'').slice(0,10)}</p><p>${o.laundry_name}<br/>${o.customer_name} · ${o.customer_phone||''}<br/>${o.address}</p><table>${rows}<tr><td>Delivery</td><td style="text-align:right">£${(o.delivery_fee||0).toFixed(2)}</td></tr>${o.credit_applied?`<tr><td>Credit</td><td style="text-align:right">-£${o.credit_applied.toFixed(2)}</td></tr>`:''}<tr class="t"><td>Total</td><td style="text-align:right">£${o.total.toFixed(2)}</td></tr></table><p style="margin-top:30px;color:#64748b">Payment: ${o.payment_status} · Status: ${o.status}</p><script>window.print()</script></body></html>`);
  w.document.close();
}

function LabelDialog({ order }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`label-${order.id}`}><QrCode className="mr-1.5 h-4 w-4" /> Label</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Order label</DialogTitle></DialogHeader>
        <div className="print-area rounded-lg border-2 border-dashed border-slate-300 p-6 text-center">
          <p className="font-heading text-xl font-bold text-slate-900">Just Laundry</p>
          <p className="font-mono text-lg font-bold">#{order.id.slice(0, 8).toUpperCase()}</p>
          <div className="my-3 flex justify-center"><QRCodeCanvas value={order.id} size={140} /></div>
          <p className="font-medium text-slate-900">{order.customer_name}</p>
          <p className="text-sm text-slate-500">{order.laundry_name}</p>
          <p className="mt-1 text-sm text-slate-500">{order.items.reduce((a, b) => a + b.qty, 0)} items · £{order.total.toFixed(2)}</p>
        </div>
        <DialogFooter><Button onClick={() => window.print()} className="bg-brand hover:bg-brand-hover" data-testid={`print-label-${order.id}`}>Print label</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-400">Loading…</div>;
  const cards = [
    ["Total revenue", `£${d.total_revenue.toFixed(2)}`],
    ["Commission earned", `£${d.total_commission.toFixed(2)}`],
    ["Paid orders", d.total_orders],
    ["Avg order value", `£${d.aov.toFixed(2)}`],
  ];
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Sales dashboard</h1>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-6"><p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">{l}</p><p className="mt-3 font-heading text-3xl font-bold text-slate-900">{v}</p></div>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading text-lg font-medium text-slate-900">Revenue (last 14 days)</h3>
          <div className="mt-4 h-64" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.revenue_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={(x) => x.slice(5)} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading text-lg font-medium text-slate-900">Revenue by laundry</h3>
          <div className="mt-4 h-64" data-testid="laundry-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.by_laundry}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" fontSize={10} tickFormatter={(x) => (x || "").slice(0, 8)} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="revenue" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {d.total_orders === 0 && <p className="mt-4 text-sm text-slate-400">No paid orders yet — charts will populate as orders are paid.</p>}
    </div>
  );
}

function AllOrders() {
  const [orders, setOrders] = useState([]);
  const [laundries, setLaundries] = useState([]);
  const [q, setQ] = useState("");
  const [laundryId, setLaundryId] = useState("all");
  const [loading, setLoading] = useState(true);
  const load = (query = q, lid = laundryId) => {
    setLoading(true);
    const params = {};
    if (query) params.q = query;
    if (lid && lid !== "all") params.laundry_id = lid;
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/admin/laundries").then((r) => setLaundries(r.data)); }, []);
  const setPayment = async (id, payment_status) => { await api.put(`/admin/orders/${id}/payment-status`, { payment_status }); load(); toast.success(`Payment marked ${payment_status}`); };
  const colors = { pending: "bg-amber-100 text-amber-700", confirmed: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700" };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">All platform orders</h1>
      <p className="mt-1 text-slate-500">Every order across all laundries. Filter by laundry, correct payment status, cancel or refund.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)} placeholder="Search by customer, laundry, address or order ID…" className="max-w-sm" data-testid="admin-orders-search" />
        <Select value={laundryId} onValueChange={(v) => { setLaundryId(v); load(q, v); }}>
          <SelectTrigger className="w-56" data-testid="admin-orders-laundry-filter"><SelectValue placeholder="All laundries" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All laundries</SelectItem>
            {laundries.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => load(q)} className="bg-brand hover:bg-brand-hover" data-testid="admin-orders-search-btn">Search</Button>
      </div>
      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
            <tr><th className="px-5 py-3">Order</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Laundry</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Payment</th><th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>}
            {!loading && orders.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">No orders found.</td></tr>}
            {!loading && orders.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50" data-testid={`admin-order-${o.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-slate-500">#{o.id.slice(0, 8)}<div className="text-slate-400">{(o.created_at || "").slice(0, 10)}</div></td>
                <td className="px-5 py-3 font-medium text-slate-900">{o.customer_name}<div className="text-xs text-slate-400">{o.customer_phone || "—"}</div></td>
                <td className="px-5 py-3 text-slate-600">{o.laundry_name}{o.coupon && <div className="text-xs text-amber-600">coupon {o.coupon.code}</div>}</td>
                <td className="px-5 py-3"><Badge className={`${colors[o.status] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>{o.status.replace("_", " ")}</Badge></td>
                <td className="px-5 py-3">
                  <Select value={o.payment_status} onValueChange={(v) => setPayment(o.id, v)}>
                    <SelectTrigger className="h-8 w-32 capitalize" data-testid={`payment-status-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["pending", "unpaid", "paid", "refunded"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3 text-right font-mono">£{o.total.toFixed(2)}</td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1">
                    <LabelDialog order={o} />
                    <Button size="sm" variant="ghost" onClick={() => openInvoice(o)} data-testid={`admin-invoice-${o.id}`}><FileText className="mr-1.5 h-4 w-4" /> Invoice</Button>
                    {(o.payment_status === "paid" || o.payment_status === "partially_refunded") && <RefundDialog order={o} reload={() => load(q)} />}
                    {o.status !== "cancelled" && o.payment_status !== "paid" && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => { await api.post(`/admin/orders/${o.id}/cancel`); load(q); toast.success("Order cancelled"); }} data-testid={`cancel-order-${o.id}`}><XCircle className="mr-1.5 h-4 w-4" /> Cancel</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RefundDialog({ order, reload }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("full");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { reason };
      if (mode === "partial") payload.amount = parseFloat(amount || 0);
      const { data } = await api.post(`/admin/orders/${order.id}/refund`, payload);
      toast.success(data.auto_refunded ? "Refund issued via Stripe" : "Refund recorded" + (data.note ? ` — ${data.note}` : ""));
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" className="text-amber-700" data-testid={`refund-order-${order.id}`}><RotateCcw className="mr-1.5 h-4 w-4" /> Refund</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Refund order #{order.id.slice(0, 8).toUpperCase()}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Order total: <b>£{order.total.toFixed(2)}</b></p>
          <div className="flex gap-2">
            <button onClick={() => setMode("full")} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${mode === "full" ? "border-brand bg-accent text-brand" : "border-slate-200"}`} data-testid="refund-mode-full">Full refund</button>
            <button onClick={() => setMode("partial")} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${mode === "partial" ? "border-brand bg-accent text-brand" : "border-slate-200"}`} data-testid="refund-mode-partial">Partial</button>
          </div>
          {mode === "partial" && <div className="space-y-1.5"><Label>Refund amount (£)</Label><Input type="number" step="0.01" max={order.total} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" /></div>}
          <div className="space-y-1.5"><Label>Reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Customer complaint…" data-testid="refund-reason" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-refund-btn">{busy ? "Processing…" : "Issue refund"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/drivers").then((r) => setDrivers(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.put(`/admin/drivers/${id}`, { status }); load(); toast.success(`Driver ${status}`); };
  const setMax = async (id, max_jobs_per_hour) => { await api.put(`/admin/drivers/${id}`, { max_jobs_per_hour: parseInt(max_jobs_per_hour || 0) }); toast.success("Limit saved"); };
  const del = async (id) => { await api.delete(`/admin/drivers/${id}`); load(); toast.success("Driver removed"); };
  const colors = { approved: "border-green-300 text-green-700", pending: "border-amber-300 text-amber-700", suspended: "border-red-300 text-red-700" };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Drivers</h1>
      <p className="mt-1 text-slate-500">Approve or suspend drivers, cap their jobs per time slot, and see who's on a job right now.</p>
      {loading && <p className="mt-6 text-slate-400">Loading…</p>}
      <div className="mt-6 space-y-4">
        {!loading && drivers.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">No drivers yet.</div>}
        {drivers.map((d) => (
          <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`admin-driver-${d.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-heading text-lg font-medium text-slate-900">{d.name}</p>
                <p className="mt-1 text-sm text-slate-500">{d.email} · {d.phone || "no phone"}</p>
                <p className="mt-1 text-sm text-slate-400">{d.completed_count} completed · £{d.earnings.toFixed(2)} earned · £{d.balance.toFixed(2)} unpaid balance</p>
              </div>
              <Badge variant="outline" className={`${colors[d.status]} capitalize`} data-testid={`driver-status-${d.id}`}>{d.status}</Badge>
            </div>
            {d.active_jobs?.length > 0 && (
              <div className="mt-3 rounded-lg bg-slate-50 p-3" data-testid={`driver-active-${d.id}`}>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">On {d.active_jobs.length} job(s)</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {d.active_jobs.map((j) => <Badge key={j.id} variant="secondary" className="capitalize">{j.job_type} · {j.order_ref} · {j.status.replace(/_/g, " ")}</Badge>)}
                </div>
              </div>
            )}
            {d.application && Object.keys(d.application).length > 0 && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`driver-application-${d.id}`}>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Application · {d.application_status || "incomplete"}</p>
                <div className="mt-2 grid gap-1 text-sm text-slate-600 sm:grid-cols-2">
                  <span>DOB: {d.application.dob || "—"}</span>
                  <span>NI: {d.application.ni_number || "—"}</span>
                  <span>Vehicle: {d.application.vehicle_type || "—"} ({d.application.vehicle_registration || "—"})</span>
                  <span>Address: {d.application.address || "—"}</span>
                  {d.application.is_business && <span className="sm:col-span-2">Business: {d.application.company_name} / {d.application.company_number} / VAT {d.application.vat_number}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  {[["photo_id_file", `Photo ID (${d.application.photo_id_type || ""})`], ["insurance_file", "Insurance"], ["proof_of_address_file", "Proof of address"], ["work_permit_file", "Work permit"]].map(([k, label]) => d.application[k] && (
                    <a key={k} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.application[k]}?auth=${localStorage.getItem("wl_token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-brand hover:underline" data-testid={`driver-doc-${k}-${d.id}`}><FileText className="h-4 w-4" /> {label}</a>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              {d.status !== "approved" && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setStatus(d.id, "approved")} data-testid={`approve-driver-${d.id}`}><Check className="mr-1.5 h-4 w-4" /> Approve</Button>}
              {d.status === "approved" && <Button size="sm" variant="secondary" onClick={() => setStatus(d.id, "suspended")} data-testid={`suspend-driver-${d.id}`}><Pause className="mr-1.5 h-4 w-4" /> Suspend</Button>}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Max jobs / slot:</span>
                <Input type="number" min="0" defaultValue={d.max_jobs_per_hour || 0} className="h-8 w-20" onBlur={(e) => setMax(d.id, e.target.value)} data-testid={`driver-maxjobs-${d.id}`} />
                <span className="text-xs text-slate-400">(0 = unlimited)</span>
              </div>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(d.id)} data-testid={`delete-driver-${d.id}`}><Trash2 className="mr-1.5 h-4 w-4" /> Remove</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Payouts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/payouts").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const pay = async (id) => { await api.post(`/admin/payouts/${id}/pay`, { note: "" }); load(); toast.success("Marked paid"); };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Driver payouts</h1>
      <p className="mt-1 text-slate-500">Read-only overview. Drivers are now paid directly by each laundrette — settle requests from the laundry's own dashboard.</p>
      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
            <tr><th className="px-5 py-3">Driver</th><th className="px-5 py-3">Requested</th><th className="px-5 py-3">Jobs</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No payout requests yet.</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50" data-testid={`payout-${p.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{p.driver_name}</td>
                <td className="px-5 py-3 text-slate-500">{(p.requested_at || "").replace("T", " ").slice(0, 16)}</td>
                <td className="px-5 py-3 text-slate-500">{(p.job_ids || []).length}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold">£{p.amount.toFixed(2)}</td>
                <td className="px-5 py-3"><Badge variant="outline" className={p.status === "paid" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{p.status}{p.paid_at ? ` · ${p.paid_at.slice(0, 10)}` : ""}</Badge></td>
                <td className="px-5 py-3 text-right">{p.status !== "paid" && <span className="text-xs text-slate-400">awaiting laundrette</span>}{p.status === "paid" && <span className="text-xs text-green-600">paid</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TEMPLATE_LABELS = {
  order_confirmation: "Order confirmation", order_status: "Order status update",
  plan_limit: "Plan limit alert", payout_paid: "Driver payout sent", refund: "Refund issued",
  delivery_reminder: "Delivery reminder (SMS text + email fallback, sent 6pm the day before)",
  rating_prompt: "Rating prompt (SMS text + email fallback, sent 10am the day after delivery — keep {{rating_link}})",
  low_rating: "Low-rating alert (emailed to the laundry and your alert email for 1–2 star ratings)",
};

function EmailTemplates() {
  const [tmpls, setTmpls] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/email-templates").then((r) => setTmpls(r.data)); }, []);
  if (!tmpls) return <div className="text-slate-400">Loading…</div>;
  const setField = (key, field, v) => setTmpls({ ...tmpls, [key]: { ...tmpls[key], [field]: v } });
  const save = async () => { setSaving(true); await api.put("/admin/email-templates", { templates: tmpls }); setSaving(false); toast.success("Email templates saved"); };
  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Email templates</h1>
      <p className="mt-1 text-slate-500">Edit every automated email. Use placeholders like {"{{customer_name}}"}, {"{{order_ref}}"}, {"{{status}}"}, {"{{amount}}"}, {"{{total}}"}.</p>
      <div className="mt-6 space-y-5">
        {Object.keys(tmpls).map((key) => (
          <div key={key} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`email-template-${key}`}>
            <h3 className="font-heading text-lg font-medium text-slate-900">{TEMPLATE_LABELS[key] || key}</h3>
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5"><Label>Subject</Label><Input value={tmpls[key].subject || ""} onChange={(e) => setField(key, "subject", e.target.value)} data-testid={`email-subject-${key}`} /></div>
              <div className="space-y-1.5"><Label>Body</Label><Textarea rows={5} value={tmpls[key].body || ""} onChange={(e) => setField(key, "body", e.target.value)} data-testid={`email-body-${key}`} /></div>
            </div>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 bg-brand hover:bg-brand-hover" data-testid="save-emails-btn">Save templates</Button>
    </div>
  );
}

function Account() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: f.name, email: f.email };
      if (f.new_password) { payload.current_password = f.current_password; payload.new_password = f.new_password; }
      await api.put("/auth/profile", payload);
      toast.success("Account updated");
      setF({ ...f, current_password: "", new_password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">My account</h1>
      <p className="mt-1 text-slate-500">Update your login email and password.</p>
      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={set("name")} data-testid="account-name" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="account-email" /></div>
        <div className="border-t border-slate-100 pt-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Change password (optional)</div>
        <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={f.current_password} onChange={set("current_password")} data-testid="account-current-password" /></div>
        <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={f.new_password} onChange={set("new_password")} data-testid="account-new-password" /></div>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover" data-testid="save-account-btn">Save changes</Button>
      </div>
    </div>
  );
}

function Prospects({ reloadLaundries }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ name: "", contact_name: "", email: "", phone: "", city: "", notes: "" });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/prospects").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name) return toast.error("Business name is required");
    await api.post("/admin/prospects", f); await load(); setOpen(false);
    setF({ name: "", contact_name: "", email: "", phone: "", city: "", notes: "" });
    toast.success("Prospect added");
  };
  const setStatus = async (id, status) => { await api.put(`/admin/prospects/${id}/status`, { status }); load(); toast.success(`Marked ${status.replace("_", " ")}`); };
  const del = async (id) => { await api.delete(`/admin/prospects/${id}`); load(); toast.success("Removed"); };
  const convert = async (id) => {
    try { await api.post(`/admin/prospects/${id}/convert`, { origin_url: window.location.origin }); await load(); reloadLaundries(); toast.success("Converted to a live laundry — set-password email sent"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const colors = { new: "border-slate-300 text-slate-600", interested: "border-blue-300 text-blue-700", not_interested: "border-red-300 text-red-600", converted: "border-green-300 text-green-700" };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Prospective laundries</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-hover" data-testid="add-prospect-btn"><Plus className="mr-2 h-4 w-4" /> Add prospect</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New prospect lead</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Business name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="prospect-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Contact name</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>City</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="prospect-email" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={add} className="bg-brand hover:bg-brand-hover" data-testid="save-prospect-btn">Add prospect</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <p className="mt-1 text-slate-500">Canvas potential clients. Mark them interested/not interested, then convert a lead to a live laundry when they sign up.</p>
      <div className="mt-6 space-y-4">
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">No prospects yet.</div>}
        {rows.map((p) => (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`prospect-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-heading text-lg font-medium text-slate-900">{p.name}{p.source === "partner_page" && <Badge variant="outline" className="ml-2 border-brand/30 align-middle text-xs text-brand" data-testid={`prospect-source-${p.id}`}>Partner page</Badge>}</p>
                <p className="mt-1 text-sm text-slate-500">{p.contact_name || "—"} · {p.email || "no email"} · {p.phone || "no phone"}</p>
                <p className="mt-1 whitespace-pre-line text-sm text-slate-400">{p.city || "—"}{p.notes ? ` · ${p.notes}` : ""}</p>
              </div>
              <Badge variant="outline" className={`${colors[p.status]} capitalize`} data-testid={`prospect-status-${p.id}`}>{p.status.replace("_", " ")}</Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              {p.status !== "converted" && <>
                <Button size="sm" variant="secondary" onClick={() => setStatus(p.id, "interested")} data-testid={`prospect-interested-${p.id}`}>Interested</Button>
                <Button size="sm" variant="secondary" onClick={() => setStatus(p.id, "not_interested")} data-testid={`prospect-notinterested-${p.id}`}>Not interested</Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => convert(p.id)} data-testid={`prospect-convert-${p.id}`}><Check className="mr-1.5 h-4 w-4" /> Convert to laundry</Button>
              </>}
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(p.id)} data-testid={`prospect-delete-${p.id}`}><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CouponForm({ onSave, testid }) {
  const [f, setF] = useState({ code: "", discount_type: "percent", value: "", max_uses: "", expires_at: "" });
  const submit = async () => {
    if (!f.code || !f.value) return toast.error("Code and value are required");
    await onSave({ code: f.code, discount_type: f.discount_type, value: parseFloat(f.value), active: true, max_uses: f.max_uses ? parseInt(f.max_uses) : 0, expires_at: f.expires_at || null });
    setF({ code: "", discount_type: "percent", value: "", max_uses: "", expires_at: "" });
  };
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="space-y-1.5"><Label className="text-xs">Code</Label><Input className="h-9 w-32 uppercase" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid={`${testid}-code`} /></div>
      <div className="space-y-1.5"><Label className="text-xs">Type</Label>
        <Select value={f.discount_type} onValueChange={(v) => setF({ ...f, discount_type: v })}>
          <SelectTrigger className="h-9 w-28" data-testid={`${testid}-type`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="fixed">Fixed £</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5"><Label className="text-xs">Value</Label><Input className="h-9 w-24" type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid={`${testid}-value`} /></div>
      <div className="space-y-1.5"><Label className="text-xs">Max uses (0=∞)</Label><Input className="h-9 w-24" type="number" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} /></div>
      <div className="space-y-1.5"><Label className="text-xs">Expires</Label><Input className="h-9 w-40" type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} /></div>
      <Button className="h-9 bg-brand hover:bg-brand-hover" onClick={submit} data-testid={`${testid}-save`}><Plus className="mr-1.5 h-4 w-4" /> Add</Button>
    </div>
  );
}

function CouponList({ coupons, onToggle, onDelete }) {
  return (
    <div className="mt-4 space-y-2">
      {coupons.length === 0 && <p className="text-sm text-slate-400">No coupons yet.</p>}
      {coupons.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`coupon-${c.id}`}>
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-slate-900">{c.code}</span>
            <Badge variant="secondary">{c.discount_type === "percent" ? `${c.value}% off` : `£${c.value} off`}</Badge>
            <span className="text-xs text-slate-400">{c.used_count} used{c.max_uses ? ` / ${c.max_uses}` : ""}{c.expires_at ? ` · exp ${c.expires_at.slice(0, 10)}` : ""}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => onToggle(c)} data-testid={`coupon-toggle-${c.id}`}>{c.active ? "Disable" : "Enable"}</Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(c.id)} data-testid={`coupon-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [claims, setClaims] = useState([]);
  const loadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  const loadClaims = () => api.get("/admin/coupon-claims").then((r) => setClaims(r.data));
  useEffect(() => { loadCoupons(); loadClaims(); }, []);
  const add = async (payload) => { try { await api.post("/admin/coupons", payload); loadCoupons(); toast.success("Coupon created"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggle = async (c) => { await api.put(`/admin/coupons/${c.id}`, { code: c.code, discount_type: c.discount_type, value: c.value, active: !c.active, max_uses: c.max_uses, expires_at: c.expires_at }); loadCoupons(); };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); loadCoupons(); toast.success("Deleted"); };
  const settle = async (id) => { await api.post(`/admin/coupon-claims/${id}/settle`); loadClaims(); toast.success("Claim settled"); };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Platform coupons</h1>
      <p className="mt-1 text-slate-500">Admin coupons work across all laundries. Laundries can reclaim the discounted amount from you — settle their claims below.</p>
      <div className="mt-6"><CouponForm onSave={add} testid="admin-coupon" /></div>
      <CouponList coupons={coupons} onToggle={toggle} onDelete={del} />

      <h2 className="mt-10 font-heading text-xl font-bold text-slate-900">Claim-back requests</h2>
      <p className="mt-1 text-sm text-slate-500">Laundries request reimbursement for admin-coupon discounts they absorbed.</p>
      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-400">
            <tr><th className="px-5 py-3">Laundry</th><th className="px-5 py-3">Orders</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Action</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {claims.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No claims yet.</td></tr>}
            {claims.map((c) => (
              <tr key={c.id} data-testid={`claim-${c.id}`}>
                <td className="px-5 py-3 font-medium">{c.laundry_name}</td>
                <td className="px-5 py-3 text-slate-500">{(c.order_ids || []).length}</td>
                <td className="px-5 py-3 text-right font-mono">£{c.amount.toFixed(2)}</td>
                <td className="px-5 py-3"><Badge variant="outline" className={c.status === "settled" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{c.status}</Badge></td>
                <td className="px-5 py-3 text-right">{c.status !== "settled" && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => settle(c.id)} data-testid={`settle-claim-${c.id}`}>Mark settled</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkEmail() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!subject || !body) return toast.error("Subject and body are required");
    setBusy(true);
    try { const { data } = await api.post("/admin/bulk-email", { subject, body }); toast.success(`Queued for ${data.recipients} active laundr${data.recipients === 1 ? "y" : "ies"}`); setSubject(""); setBody(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Bulk email to laundries</h1>
      <p className="mt-1 text-slate-500">Send one message to every active laundry manager (uses your SMTP settings).</p>
      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="space-y-1.5"><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="bulk-email-subject" /></div>
        <div className="space-y-1.5"><Label>Message</Label><Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="bulk-email-body" /></div>
        <Button onClick={send} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="bulk-email-send-btn">{busy ? "Sending…" : "Send to all active laundries"}</Button>
      </div>
    </div>
  );
}

function Complaints() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/complaints").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const [resp, setResp] = useState({});
  const update = async (id, status) => { await api.put(`/admin/complaints/${id}`, { status, admin_response: resp[id] }); load(); toast.success("Complaint updated"); };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Complaints</h1>
      <p className="mt-1 text-slate-500">Issues raised by customers (about laundries or drivers) and by laundries (about drivers).</p>
      <div className="mt-6 space-y-4">
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">No complaints yet.</div>}
        {rows.map((c) => (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`complaint-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-heading text-lg font-medium text-slate-900">{c.subject}</p>
                <p className="mt-1 text-sm text-slate-500">{c.raised_by_name} ({c.raised_by_role}) → about {c.against_type} <b>{c.against_name}</b>{c.order_id ? ` · order #${c.order_id.slice(0, 8)}` : ""}</p>
              </div>
              <Badge variant="outline" className={c.status === "resolved" ? "border-green-300 text-green-700" : c.status === "in_review" ? "border-blue-300 text-blue-700" : "border-amber-300 text-amber-700"}>{c.status}</Badge>
            </div>
            <p className="mt-3 text-sm text-slate-700">{c.message}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
              <Input placeholder="Response to the complainant…" defaultValue={c.admin_response} onChange={(e) => setResp({ ...resp, [c.id]: e.target.value })} className="max-w-md" data-testid={`complaint-response-${c.id}`} />
              <Button size="sm" variant="secondary" onClick={() => update(c.id, "in_review")} data-testid={`complaint-review-${c.id}`}>Mark in review</Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => update(c.id, "resolved")} data-testid={`complaint-resolve-${c.id}`}>Resolve</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TermsEditor() {
  const [t, setT] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/public/terms").then((r) => setT(r.data)); }, []);
  const save = async () => { setSaving(true); await api.put("/admin/terms", t); setSaving(false); toast.success("Terms & Conditions saved"); };
  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Terms &amp; Conditions</h1>
      <p className="mt-1 text-slate-500">Published at <b>/terms</b>. Drivers must accept these during their application.</p>
      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="space-y-1.5"><Label>Title</Label><Input value={t.title || ""} onChange={(e) => setT({ ...t, title: e.target.value })} data-testid="terms-title" /></div>
        <div className="space-y-1.5"><Label>Body</Label><Textarea rows={16} value={t.body || ""} onChange={(e) => setT({ ...t, body: e.target.value })} data-testid="terms-body" /></div>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover" data-testid="save-terms-btn">Save terms</Button>
      </div>
    </div>
  );
}

function Branding() {
  const { refresh } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data.site)); }, []);
  if (!s) return <div className="text-slate-400">Loading…</div>;
  const set = (k) => (v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { site: s }); await refresh(); toast.success("Branding updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Branding</h1>
      <p className="mt-1 text-slate-500">Control the site name, logo, browser tab title and colour scheme.</p>
      <div className="mt-6 space-y-5 rounded-xl border border-slate-200 bg-white p-6">
        <Field label="Site name" value={s.site_name} onChange={set("site_name")} data-testid="brand-site-name" />
        <Field label="Browser tab title (page title)" value={s.page_title} onChange={set("page_title")} data-testid="brand-page-title" />
        <Field label="Tagline" value={s.tagline} onChange={set("tagline")} data-testid="brand-tagline" />
        <div className="space-y-1.5">
          <Field label="Logo image URL (leave blank to use the text name)" value={s.logo_url} onChange={set("logo_url")} data-testid="brand-logo-url" placeholder="https://…/logo.png" />
          {s.logo_url ? <img src={s.logo_url} alt="logo preview" className="mt-2 h-8 w-auto" /> : null}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Primary colour</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={s.primary_color || "#2563EB"} onChange={(e) => set("primary_color")(e.target.value)} className="h-10 w-14 cursor-pointer rounded border border-slate-200" data-testid="brand-primary-color" />
              <Input value={s.primary_color || ""} onChange={(e) => set("primary_color")(e.target.value)} className="font-mono" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Accent colour</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={s.secondary_color || "#F97316"} onChange={(e) => set("secondary_color")(e.target.value)} className="h-10 w-14 cursor-pointer rounded border border-slate-200" data-testid="brand-secondary-color" />
              <Input value={s.secondary_color || ""} onChange={(e) => set("secondary_color")(e.target.value)} className="font-mono" />
            </div>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover" data-testid="save-branding-btn">Save branding</Button>
      </div>
    </div>
  );
}

function Staff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields required");
    try { await api.post("/admin/staff", form); setForm({ name: "", email: "", password: "", role: "staff" }); setOpen(false); await load(); toast.success("Team member added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/staff/${id}`); await load(); toast.success("Removed"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Team management</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-hover" data-testid="add-staff-btn"><Plus className="mr-2 h-4 w-4" /> Add member</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add admin or staff</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></div>
              <div className="space-y-1.5"><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" /></div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff — laundry signups & order support</SelectItem>
                    <SelectItem value="admin">Admin — full access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={add} className="bg-brand hover:bg-brand-hover" data-testid="save-staff-btn">Add member</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="mt-6 space-y-3">
        {staff.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-6 py-4" data-testid={`staff-row-${m.id}`}>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-brand"><UserCog className="h-5 w-5" strokeWidth={1.5} /></span>
              <div>
                <p className="font-medium text-slate-900">{m.name} {m.is_self && <span className="text-xs text-slate-400">(you)</span>}</p>
                <p className="text-sm text-slate-500">{m.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className={m.role === "admin" ? "border-brand text-brand capitalize" : "capitalize"}>{m.role}</Badge>
              {!m.is_self && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(m.id)} data-testid={`delete-staff-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Clothing({ clothing, reload }) {
  const [form, setForm] = useState({ name: "", category: "General" });
  const add = async () => {
    if (!form.name) return toast.error("Name required");
    try { await api.post("/admin/clothing-types", form); setForm({ name: "", category: "General" }); await reload(); toast.success("Garment added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/clothing-types/${id}`); await reload(); toast.success("Removed"); };
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Default garment catalogue</h1>
      <p className="mt-1 text-slate-500">These appear as quick-add options for every laundry manager.</p>
      <div className="mt-6 flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Garment name" className="w-48" data-testid="clothing-name-input" />
        <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className="w-40" data-testid="clothing-category-input" />
        <Button onClick={add} className="bg-brand hover:bg-brand-hover" data-testid="add-clothing-btn"><Plus className="mr-2 h-4 w-4" /> Add</Button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {clothing.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`clothing-${c.id}`}>
            <div><p className="font-medium text-slate-900">{c.name}</p><p className="text-xs text-slate-400">{c.category}</p></div>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(c.id)} data-testid={`delete-clothing-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const CAPS = [
  { key: "sms", label: "SMS notifications" },
  { key: "drivers", label: "Driver dispatch / delivery" },
  { key: "custom_items", label: "Custom garments (beyond default catalogue)" },
  { key: "analytics", label: "Analytics" },
  { key: "multi_location", label: "Multiple locations" },
];

function PackageDialog({ trigger, onSave, initial }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || { name: "", price: "", order_limit: 0, features: "", capabilities: {} });
  const toggleCap = (k) => setForm({ ...form, capabilities: { ...form.capabilities, [k]: !form.capabilities[k] } });
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      await onSave({
        name: form.name, price: parseFloat(form.price || 0), interval: "month",
        order_limit: parseInt(form.order_limit || 0, 10),
        features: typeof form.features === "string" ? form.features.split("\n").filter(Boolean) : form.features,
        capabilities: form.capabilities,
      });
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="package-name-input" /></div>
            <div className="space-y-1.5"><Label>Price / month (£)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="package-price-input" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Monthly order limit (0 = unlimited)</Label>
            <Input type="number" value={form.order_limit} onChange={(e) => setForm({ ...form, order_limit: e.target.value })} data-testid="package-limit-input" />
          </div>
          <div className="space-y-2">
            <Label>What laundries on this plan can do</Label>
            {CAPS.map((c) => (
              <label key={c.key} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                {c.label}
                <Switch checked={!!form.capabilities[c.key]} onCheckedChange={() => toggleCap(c.key)} data-testid={`cap-${c.key}`} />
              </label>
            ))}
          </div>
          <div className="space-y-1.5"><Label>Marketing features (one per line)</Label><textarea className="min-h-20 w-full rounded-md border border-input p-3 text-sm" value={typeof form.features === "string" ? form.features : (form.features || []).join("\n")} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="package-features-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-brand hover:bg-brand-hover" data-testid="save-package-btn">{initial ? "Save changes" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Packages({ packages, reload }) {
  const add = async (data) => { await api.post("/admin/packages", data); await reload(); toast.success("Package added"); };
  const update = (id) => async (data) => { await api.put(`/admin/packages/${id}`, data); await reload(); toast.success("Package updated"); };
  const del = async (id) => { await api.delete(`/admin/packages/${id}`); await reload(); toast.success("Removed"); };
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Subscription packages</h1>
        <PackageDialog onSave={add} trigger={<Button className="bg-brand hover:bg-brand-hover" data-testid="add-package-btn"><Plus className="mr-2 h-4 w-4" /> New package</Button>} />
      </div>
      <p className="mt-1 text-slate-500">Define pricing, monthly order limits and what each plan can do.</p>
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        {packages.map((p) => (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`package-${p.id}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-heading text-lg font-medium text-slate-900">{p.name}</h3>
              <div className="flex gap-1">
                <PackageDialog initial={{ name: p.name, price: String(p.price), order_limit: p.order_limit || 0, features: (p.features || []).join("\n"), capabilities: p.capabilities || {} }} onSave={update(p.id)} trigger={<Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-package-${p.id}`}><Pencil className="h-4 w-4" /></Button>} />
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(p.id)} data-testid={`delete-package-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <p className="mt-2 font-heading text-3xl font-bold text-slate-900">£{p.price.toFixed(0)}<span className="text-base font-normal text-slate-400">/{p.interval}</span></p>
            <p className="mt-1 text-sm font-medium text-slate-500">{p.order_limit ? `${p.order_limit} orders / month` : "Unlimited orders"}</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">{(p.features || []).map((f, i) => <li key={i} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> {f}</li>)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      {textarea ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} {...props} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} {...props} />
      )}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stripe, setStripe] = useState({ secret_key: "", webhook_secret: "", publishable_key: "" });
  const [twilio, setTwilio] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [smtp, setSmtp] = useState({ host: "", port: "", username: "", password: "", from_email: "", use_ssl: false });

  const load = () => api.get("/admin/settings").then((r) => {
    setS(r.data);
    setStripe({ secret_key: "", webhook_secret: "", publishable_key: r.data.stripe.publishable_key || "" });
    setTwilio({ account_sid: r.data.twilio.account_sid || "", auth_token: "", from_number: r.data.twilio.from_number || "" });
    setSmtp({ host: r.data.smtp.host || "", port: r.data.smtp.port || "", username: r.data.smtp.username || "", password: "", from_email: r.data.smtp.from_email || "", use_ssl: !!r.data.smtp.use_ssl });
  });
  useEffect(() => { load(); }, []);
  if (!s) return <div className="text-slate-400">Loading…</div>;

  const saveGeneral = async () => {
    setSaving(true);
    await api.put("/admin/settings", { commission_rate: parseFloat(s.commission_rate), currency: s.currency, driver_fee: parseFloat(s.driver_fee || 0), maps_api_key: s.maps_api_key || "", partner_commission_rate: parseFloat(s.partner_commission_rate || 0), default_service_radius_km: parseFloat(s.default_service_radius_km || 0), alert_email: s.alert_email || "" });
    setSaving(false); toast.success("General settings saved");
  };
  const saveStripe = async () => {
    const payload = { stripe: { connect_enabled: s.stripe.connect_enabled } };
    if (stripe.secret_key) payload.stripe.secret_key = stripe.secret_key;
    if (stripe.webhook_secret) payload.stripe.webhook_secret = stripe.webhook_secret;
    if (stripe.publishable_key) payload.stripe.publishable_key = stripe.publishable_key;
    await api.put("/admin/settings", payload);
    await load(); toast.success("Stripe settings saved");
  };
  const saveTwilio = async () => {
    const payload = { twilio: { account_sid: twilio.account_sid, from_number: twilio.from_number } };
    if (twilio.auth_token) payload.twilio.auth_token = twilio.auth_token;
    await api.put("/admin/settings", payload);
    await load(); toast.success("Twilio settings saved");
  };
  const saveSmtp = async () => {
    const payload = { smtp: { host: smtp.host, port: smtp.port, username: smtp.username, from_email: smtp.from_email, use_ssl: smtp.use_ssl } };
    if (smtp.password) payload.smtp.password = smtp.password;
    await api.put("/admin/settings", payload);
    await load(); toast.success("SMTP settings saved");
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Settings & API keys</h1>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">General</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Commission rate (%)</Label>
            <Input type="number" step="0.1" value={s.commission_rate} onChange={(e) => setS({ ...s, commission_rate: e.target.value })} data-testid="commission-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Currency (ISO code)</Label>
            <Input value={s.currency} onChange={(e) => setS({ ...s, currency: e.target.value })} data-testid="currency-input" placeholder="gbp" />
          </div>
          <div className="space-y-1.5">
            <Label>Driver fee per job (£)</Label>
            <Input type="number" step="0.01" value={s.driver_fee ?? ""} onChange={(e) => setS({ ...s, driver_fee: e.target.value })} data-testid="driver-fee-input" placeholder="3.50" />
          </div>
          <div className="space-y-1.5">
            <Label>Maps API key (for driver route optimisation — later)</Label>
            <Input value={s.maps_api_key ?? ""} onChange={(e) => setS({ ...s, maps_api_key: e.target.value })} data-testid="maps-key-input" placeholder="AIza… (optional)" />
          </div>
          <div className="space-y-1.5">
            <Label>Partner commission rate (%)</Label>
            <Input type="number" step="0.5" value={s.partner_commission_rate ?? ""} onChange={(e) => setS({ ...s, partner_commission_rate: e.target.value })} data-testid="partner-commission-input" placeholder="15" />
          </div>
          <div className="space-y-1.5">
            <Label>Low-rating alert email (1–2 star reviews)</Label>
            <Input type="email" value={s.alert_email ?? ""} onChange={(e) => setS({ ...s, alert_email: e.target.value })} data-testid="alert-email-input" placeholder="alerts@yourdomain.co.uk" />
          </div>
          <div className="space-y-1.5">
            <Label>Default service radius (km)</Label>
            <Input type="number" step="0.5" value={s.default_service_radius_km ?? ""} onChange={(e) => setS({ ...s, default_service_radius_km: e.target.value })} data-testid="default-radius-input" placeholder="8" />
          </div>
        </div>
        <Button onClick={saveGeneral} disabled={saving} className="mt-4 bg-brand hover:bg-brand-hover" data-testid="save-settings-btn">Save general</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-brand" /><h3 className="font-heading text-lg font-medium text-slate-900">Stripe</h3></div>
        <p className="mt-1 text-sm text-slate-500">Your own Stripe keys power customer payments and Connect payouts. Secrets are stored securely and never shown again.</p>
        <div className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Secret key {s.stripe.secret_key_set && <span className="text-green-600">· set ({s.stripe.secret_key_masked})</span>}</Label>
            <Input type="password" value={stripe.secret_key} onChange={(e) => setStripe({ ...stripe, secret_key: e.target.value })} placeholder={s.stripe.secret_key_set ? "Leave blank to keep current" : "sk_live_… / sk_test_…"} data-testid="stripe-secret-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Publishable key</Label>
            <Input value={stripe.publishable_key} onChange={(e) => setStripe({ ...stripe, publishable_key: e.target.value })} placeholder="pk_live_… / pk_test_…" data-testid="stripe-publishable-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Webhook signing secret {s.stripe.webhook_secret_set && <span className="text-green-600">· set</span>}</Label>
            <Input type="password" value={stripe.webhook_secret} onChange={(e) => setStripe({ ...stripe, webhook_secret: e.target.value })} placeholder={s.stripe.webhook_secret_set ? "Leave blank to keep current" : "whsec_…"} data-testid="stripe-webhook-input" />
            <p className="text-xs text-slate-400">Point your Stripe webhook to <span className="font-mono">/api/webhook/stripe</span> (event: checkout.session.completed).</p>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <p className="text-sm font-medium text-slate-900">Enable Stripe Connect</p>
              <p className="text-xs text-slate-500">Laundries onboard their own accounts; you take commission automatically as an application fee.</p>
            </div>
            <Switch checked={!!s.stripe.connect_enabled} onCheckedChange={(v) => setS({ ...s, stripe: { ...s.stripe, connect_enabled: v } })} data-testid="connect-toggle" />
          </label>
        </div>
        <Button onClick={saveStripe} className="mt-4 bg-brand hover:bg-brand-hover" data-testid="save-stripe-btn">Save Stripe settings</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-brand" /><h3 className="font-heading text-lg font-medium text-slate-900">Twilio SMS</h3></div>
        <p className="mt-1 text-sm text-slate-500">Shared by all laundries to text customers. Stored securely.</p>
        <div className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Account SID</Label>
            <Input value={twilio.account_sid} onChange={(e) => setTwilio({ ...twilio, account_sid: e.target.value })} placeholder="AC…" data-testid="twilio-sid-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Auth token {s.twilio.auth_token_set && <span className="text-green-600">· set ({s.twilio.auth_token_masked})</span>}</Label>
            <Input type="password" value={twilio.auth_token} onChange={(e) => setTwilio({ ...twilio, auth_token: e.target.value })} placeholder={s.twilio.auth_token_set ? "Leave blank to keep current" : "Your Twilio auth token"} data-testid="twilio-token-input" />
          </div>
          <div className="space-y-1.5">
            <Label>From number (E.164)</Label>
            <Input value={twilio.from_number} onChange={(e) => setTwilio({ ...twilio, from_number: e.target.value })} placeholder="+447700900000" data-testid="twilio-from-input" />
          </div>
        </div>
        <Button onClick={saveTwilio} className="mt-4 bg-brand hover:bg-brand-hover" data-testid="save-twilio-btn">Save Twilio settings</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-brand" /><h3 className="font-heading text-lg font-medium text-slate-900">Email (SMTP)</h3></div>
        <p className="mt-1 text-sm text-slate-500">Used for platform notifications (e.g. plan-limit alerts to laundries). Use your own SMTP server.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>SMTP host</Label><Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.yourmail.com" data-testid="smtp-host-input" /></div>
          <div className="space-y-1.5"><Label>Port</Label><Input value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} placeholder="587" data-testid="smtp-port-input" /></div>
          <div className="space-y-1.5"><Label>Username</Label><Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} data-testid="smtp-username-input" /></div>
          <div className="space-y-1.5"><Label>Password {s.smtp.password_set && <span className="text-green-600">· set</span>}</Label><Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder={s.smtp.password_set ? "Leave blank to keep current" : "App password"} data-testid="smtp-password-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>From email</Label><Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="noreply@yourmail.com" data-testid="smtp-from-input" /></div>
        </div>
        <label className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 p-3">
          <span className="text-sm text-slate-700">Use SSL (port 465). Off = STARTTLS (port 587).</span>
          <Switch checked={smtp.use_ssl} onCheckedChange={(v) => setSmtp({ ...smtp, use_ssl: v })} data-testid="smtp-ssl-toggle" />
        </label>
        <Button onClick={saveSmtp} className="mt-4 bg-brand hover:bg-brand-hover" data-testid="save-smtp-btn">Save SMTP settings</Button>
      </div>
      <TestNotifications />
    </div>
  );
}
